import {
  licensedInventoryVerdict,
  useAssetVersionQuery,
  type LibraryAsset,
  type LibraryAssetVersionDetail,
} from "@/api/library";
import { UNLICENSED_SENTENCE } from "@/lib/adoptions";
import { Badge } from "@/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { Notice } from "@/ui/Notice";
import { ErrorState, LoadingState } from "@/ui/states";

import { VersionContent } from "./VersionContent";

/**
 * One questionnaire, read-only, with the provenance that admitted it (#638).
 *
 * ## WHY THIS REPLACED AN EDITOR RATHER THAN DISABLING ONE
 *
 * `QuestionnaireAssetEditor` chose between an authoring form and a read-only
 * view, and #368 built it on a premise ratified §2.2 has now closed: that the
 * service's refusal for seeded canon — "copy it into a new asset or override
 * per project" — had a destination. It does not. The family is governed: its
 * approved inventory is seeded from the clinical catalog, `POST
 * /v1/library/assets` refuses it, and so does the version route. A copy would
 * be an instrument nobody licensed, adoptable nowhere, wearing a name
 * clinicians trust.
 *
 * So the authoring tree is GONE rather than disabled, which is that component's
 * own reasoning applied to itself: "a form with every control disabled is one
 * forgotten prop away from being live." There are no controls here to enable.
 *
 * ## IT STILL HAS TO EXPLAIN ITSELF
 *
 * A screen that merely lacks a Save button reads as a bug or a permissions
 * problem. The two facts a clinician needs are stated outright: authoring is
 * unavailable and why, and — when the catalog admitted this instrument — who
 * approved it and on what basis. That provenance is what makes the restricted
 * roster inspectable instead of arbitrary.
 *
 * ## EVERY FACT IS THE SERVER'S
 *
 * `capabilities.licensedInventory` is computed from the seed catalog's own
 * licensing declarations, by the same module `catalog_verdict` refuses on.
 * Nothing here decides whether an instrument is licensed, and nothing here
 * holds a roster of approved names.
 */

const AUTHORING_UNAVAILABLE =
  "Questionnaires cannot be authored or copied in the Editor. Their approved inventory is seeded from the clinical catalog, and new instruments arrive through clinical and licensing review — validity, scoring and versioning contracts have to be approved before an instrument can be authored here.";

function InventoryStatusUnavailable() {
  return (
    <>
      <p className="flex items-center gap-2 text-sm">
        <Badge tone="neutral">Status unavailable</Badge>
      </p>
      <p className="text-sm text-muted-foreground">
        Inventory approval details are temporarily unavailable. This instrument
        remains read-only, and the Editor Service still checks whether it can
        be newly adopted.
      </p>
    </>
  );
}

export function QuestionnaireInstrument({ asset }: { asset: LibraryAsset }) {
  const head = useAssetVersionQuery(asset.id, asset.latestVersion);
  const detail: LibraryAssetVersionDetail | undefined = head.data;
  const inventory = licensedInventoryVerdict(asset);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Inventory</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {inventory === undefined ? (
            <InventoryStatusUnavailable />
          ) : inventory.state === "included" ? (
            // A description list, because that is what these are: three
            // server-stated facts about one instrument, each with a name. The
            // provenance is published so the restricted roster is checkable
            // rather than merely asserted.
            <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[max-content_1fr]">
              <dt className="font-medium text-foreground">Approved by</dt>
              <dd className="text-muted-foreground">{inventory.authority}</dd>
              <dt className="font-medium text-foreground">Licensing basis</dt>
              <dd className="text-muted-foreground">{inventory.reference}</dd>
              <dt className="font-medium text-foreground">Approved on</dt>
              <dd className="font-mono text-muted-foreground">
                {inventory.approvedOn}
              </dd>
            </dl>
          ) : inventory.state === "excluded" ? (
            <>
              <p className="flex items-center gap-2 text-sm">
                <Badge tone="warning">Not licensed</Badge>
              </p>
              {/*
                The same sentence the adoption surfaces show, from the same
                map: a clinician meeting this instrument in the library and in
                a project's picker meets one explanation, because it is one
                string keyed by the server's own reason.
              */}
              <p className="text-sm text-muted-foreground">
                {UNLICENSED_SENTENCE[inventory.reason] ??
                  "This instrument is outside the approved clinical inventory, so it cannot be newly adopted."}
              </p>
            </>
          ) : inventory.state === "not_governed" ? (
            <p className="text-sm text-muted-foreground">
              This asset&apos;s family is not inventory-governed.
            </p>
          ) : (
            // A later service can widen the state vocabulary before this
            // bundle knows how to describe it. Do not turn that skew into a
            // route crash or guess that it means licensed or unlicensed.
            <InventoryStatusUnavailable />
          )}
        </CardBody>
      </Card>

      <Notice>{AUTHORING_UNAVAILABLE}</Notice>

      <Card>
        <CardHeader>
          <CardTitle>
            {`Instrument at ${asset.latestVersion} (read-only)`}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {head.isPending ? (
            <LoadingState label="Loading questionnaire" rows={4} />
          ) : head.isError || detail === undefined ? (
            <ErrorState
              title="This questionnaire could not be loaded"
              description={`The Editor Service did not return the content at ${asset.latestVersion}, so what this instrument administers is not known here.`}
              onRetry={() => void head.refetch()}
            />
          ) : (
            <VersionContent content={detail.content} />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
